import { useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { motion, useAnimationControls } from 'framer-motion';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useEventRotation } from '../../../../../components/LobbyPassBanner/useEventRotation';
import type { BingoResultToShowType, MetagameBingoEvent } from '../../../../../stores/bingo/types';
import { useBingoAnimationReady, useBingoWidgetCallbacks } from '../../../hooks';
import type { BingoWidgetDataType } from '../../../types';
import { BingoWidgetLogo } from '../BingoWidgetLogo/BingoWidgetLogo';
import { BingoWidgetTimer } from '../BingoWidgetTimer/BingoWidgetTimer';

import styles from './BingoSmallWidget.module.scss';

interface BingoSmallWidgetProps {
  data: BingoWidgetDataType;
}

const TIMER_BUFFER_MS = 3000;

const RewardWidget = ({ event }: { event: BingoResultToShowType }) => {
  const { onResultClick } = useBingoWidgetCallbacks();
  if (!event) {
    return null;
  }

  return (
    <div className={styles.container} role="button" onClick={() => onResultClick(event)}>
      <BingoWidgetLogo bingoType={event.coinType === 'GC' ? 'CASUAL' : 'SWEEP'} sizeType={'small'} />
    </div>
  );
};

const LiveWidget = ({ event }: { event: MetagameBingoEvent }) => {
  const { onEnterLiveEventClick } = useBingoWidgetCallbacks();
  return (
    <div className={styles.container} role="button" onClick={() => onEnterLiveEventClick(event)}>
      <BingoWidgetLogo bingoType={event.coinType === 'GC' ? 'CASUAL' : 'SWEEP'} sizeType="small" />
    </div>
  );
};

const UpcomingWidget = observer(() => {
  const { shouldRotate, upcomingEvents, displayEvent } = useEventRotation();
  const animationReadyRef = useBingoAnimationReady();
  const { onPurchaseClick } = useBingoWidgetCallbacks();
  const { t } = useTranslation();

  if (!upcomingEvents || !upcomingEvents.length) {
    return null;
  }

  const rotationEnabled = shouldRotate && !!displayEvent;
  const currentEvent = rotationEnabled ? displayEvent : upcomingEvents[0];
  const bufferedStartMs = currentEvent?.startDate ? new Date(currentEvent.startDate).getTime() + TIMER_BUFFER_MS : null;
  const timeToStartInMs = bufferedStartMs ? dayjs(bufferedStartMs).diff(dayjs(), 'milliseconds') : 0;
  const bingoType = currentEvent.coinType === 'GC' ? 'CASUAL' : 'SWEEP';
  const [deferredTimer, setDeferredTimer] = useState({ eventId: currentEvent?.id || '', startsInMs: timeToStartInMs });
  const latestTimerRef = useRef({ eventId: currentEvent?.id || '', startsInMs: timeToStartInMs });
  latestTimerRef.current = { eventId: currentEvent?.id || '', startsInMs: timeToStartInMs };

  const timerControls = useAnimationControls();

  const disappear = useCallback(async () => {
    await timerControls.start({ scale: [1, 1.1, 0], transition: { duration: 1, ease: 'easeInOut' } });
  }, [timerControls]);

  const appear = useCallback(async () => {
    await timerControls.start({ scale: [0, 1.1, 1], transition: { duration: 1, ease: 'easeInOut' } });
  }, [timerControls]);

  const rotate = useCallback(async () => {
    await disappear();
    setDeferredTimer(latestTimerRef.current);
    await appear();
  }, [disappear, appear]);

  useEffect(() => {
    if (!animationReadyRef.current) {
      return;
    }
    if (rotationEnabled) {
      void rotate();
    }
  }, [rotate, rotationEnabled, animationReadyRef, currentEvent?.id]);

  return (
    <div className={styles.container} role="button" onClick={onPurchaseClick}>
      <BingoWidgetLogo
        timerIncluded
        animationReadyRef={animationReadyRef}
        bingoType={bingoType}
        rotation={rotationEnabled}
        sizeType={'small'}
      />
      <motion.div animate={timerControls} className={styles.contentContainer}>
        <BingoWidgetTimer eventId={deferredTimer.eventId} startsInMs={deferredTimer.startsInMs} />
        <button className={styles.btn}>
          <span className={styles.btnText}>{t('plan.purchase')}</span>
        </button>
      </motion.div>
    </div>
  );
});

export function BingoSmallWidget(props: BingoSmallWidgetProps) {
  const { data } = props;
  const { event, widgetType } = data;

  switch (widgetType) {
    case 'reward':
      return <RewardWidget event={event as BingoResultToShowType} />;
    case 'upcoming':
      return <UpcomingWidget />;
    case 'live':
    case 'startingSoon':
      return <LiveWidget event={event as MetagameBingoEvent} />;
    default:
      return null;
  }
}
